import {
  isClientAuthFailure,
  type RestErrorResponse,
} from "@/models/client/RestResponse";

/**
 * Retry policy for react-query reads that go through {@link RestClient}.
 *
 * The default `retry: n` retries everything, including responses the API
 * genuinely produced. On the legislation list that turned one slow search into
 * six: the query times out at the database's 20s command timeout, returns a 500,
 * and every retry spends another 20s of database CPU on a query that cannot
 * succeed — while the user's next keystroke is already queueing its own (LAR-445).
 *
 * So the question is not "which status is it", it is "did the API answer at all".
 * A status alone cannot tell them apart: RestClient manufactures a 408 for a
 * network failure and a 401 for a token blip (LAR-361), and both of those are
 * worth another attempt even though they carry a status.
 */

/** A failed request, carrying whether the API is the one that rejected it. */
export class RequestFailedError extends Error {
  readonly status: number;
  /** False when RestClient made the failure up because it could not reach the API. */
  readonly serverAnswered: boolean;

  constructor(message: string, status: number, serverAnswered: boolean) {
    super(message);
    this.name = "RequestFailedError";
    this.status = status;
    this.serverAnswered = serverAnswered;
  }
}

/**
 * The two failures RestClient manufactures without reaching the API: a synthetic
 * 408 for a network/DNS/TLS error, and a synthetic 401 when the Auth0 SDK could
 * not supply a token (see `ClientAuthFailure`).
 */
const CLIENT_SYNTHESISED_TIMEOUT = 408;

export function requestFailedError(
  response: RestErrorResponse
): RequestFailedError {
  const serverAnswered =
    response.status !== CLIENT_SYNTHESISED_TIMEOUT &&
    !isClientAuthFailure(response);

  return new RequestFailedError(
    response.feedback.message,
    response.status,
    serverAnswered
  );
}

/**
 * @param failureCount attempts already made, as react-query counts them
 * @param error the thrown error; a {@link RequestFailedError} knows who rejected it
 * @param maxAttempts how many retries a never-answered failure is allowed
 */
export function shouldRetryRequest(
  failureCount: number,
  error: unknown,
  maxAttempts: number
): boolean {
  if (failureCount >= maxAttempts) return false;

  // The API answered, so it will answer the same way again.
  if (error instanceof RequestFailedError && error.serverAnswered) return false;

  return true;
}
